

import React, { useState, useEffect } from 'react';
import Navigation from "../Navbar/Navbar";
import axios from 'axios';
import './Home.css';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { Pagination, Card, Container, Row, Col, Form, InputGroup } from 'react-bootstrap';
import { Search, Trash } from 'react-bootstrap-icons';

const apiUrl = "/api";
const url = apiUrl + '/inventory';

const AllList = () => {
    const [allItems, setAllItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [editMode, setEditMode] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [user, setUser] = useState(null);
    const itemsPerPage = 10;

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            window.location.href = '#/login';
            return;
        }

        // Get user data
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }

        fetchAllData();
    }, []);

    useEffect(() => {
        // Filter items based on search term
        const filtered = allItems.filter(item =>
            item.Type.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.Make.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.Model.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.Serial.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.Location.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.status && item.status.toLowerCase().includes(searchTerm.toLowerCase())) ||
            item.qrid.toString().includes(searchTerm)
        );
        setFilteredItems(filtered);
        setCurrentPage(1); // Reset to first page when search changes
    }, [searchTerm, allItems]);

    const getStatusBadge = (status) => {
        const s = status || 'Received';
        let variant = 'secondary';
        if (s === 'ReadyForDispatch') variant = 'success';
        else if (s === 'Deployed') variant = 'primary';
        else if (s === 'InPrep') variant = 'warning';
        else if (s === 'Retired') variant = 'danger';

        return <span className={`badge bg-${variant}`}>{s.replace(/([A-Z])/g, ' $1').trim()}</span>;
    };

    const fetchAllData = () => {
        setLoading(true);
        const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
        const token = localStorage.getItem('token');
        // Fetch all items without pagination
        axios.get(`${url}?limit=10000&tenant_id=${tenantId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }) // Large limit to get all items
            .then(res => {
                setAllItems(res.data.data || res.data);
                setFilteredItems(res.data.data || res.data);
                setLoading(false);
            })
            .catch(err => {
                console.log(err);
                setLoading(false);
            });
    };

    const handleEditClick = (id) => {
        setEditMode(prevEditMode => ({
            ...prevEditMode,
            [id]: true
        }));
    };

    const handleSaveClick = (itemsedited) => {
        setEditMode(prevEditMode => ({
            ...prevEditMode,
            [itemsedited.id]: false
        }));
        const token = localStorage.getItem('token');
        const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
        axios.put(apiUrl + '/update-item', { ...itemsedited, tenant_id: tenantId }, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                fetchAllData(); // Refresh all data
            })
            .catch(err => {
                console.log(err);
            });
    };

    const handleDeleteClick = (itemId) => {
        if (window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
            const token = localStorage.getItem('token');
            axios.delete(`${apiUrl}/delete-item/${itemId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
                .then(res => {
                    fetchAllData(); // Refresh all data
                })
                .catch(err => {
                    console.log('Error deleting item:', err);
                    alert('Error deleting item. Please try again.');
                });
        }
    };

    // Calculate pagination
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filteredItems.slice(startIndex, endIndex);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        items.push(
            <Pagination.Prev
                key="prev"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
            />
        );

        // First page + ellipsis if needed
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }

        // Page numbers
        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        // Last page + ellipsis if needed
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        // Next button
        items.push(
            <Pagination.Next
                key="next"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
            />
        );

        return <Pagination className="justify-content-center mt-3">{items}</Pagination>;
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <div>
            <Container className="page-content-wrapper mt-4">
                <Row className="mb-4">
                    <Col>
                        <h1 className="text-center mb-4" style={{
                            background: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontSize: '2rem',
                            fontWeight: '700',
                            letterSpacing: '-0.02em'
                        }}>
                            Inventory Management
                        </h1>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search by Type, Make, Model, Serial, Location, or QR ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={6} className="text-end">
                        <small className="text-muted">
                            Showing {startIndex + 1}-{Math.min(endIndex, filteredItems.length)} of {filteredItems.length} items
                            {searchTerm && ` (filtered from ${allItems.length} total)`}
                        </small>
                    </Col>
                </Row>

                <div className="inventory-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Count</th>
                                <th>QR ID</th>
                                <th>Status</th>
                                <th>Type</th>
                                <th>Make</th>
                                <th>Model</th>
                                <th>Serial</th>
                                <th>Location</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(item => (
                                <tr key={item.id}>
                                    <td><a href={`#/inv?dbid=${item.id}`} className="inventory-link">{item.id}</a></td>
                                    <td>
                                        {editMode[item.id] ? (
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={item.qrid}
                                                onChange={(e) => {
                                                    setAllItems(prevItems =>
                                                        prevItems.map(inv =>
                                                            inv.id === item.id
                                                                ? { ...inv, qrid: e.target.value }
                                                                : inv
                                                        )
                                                    );
                                                }}
                                            />
                                        ) : (
                                            item.qrid
                                        )}
                                    </td>
                                    <td>{getStatusBadge(item.status)}</td>
                                    <td>{item.Type}</td>
                                    <td>{item.Make}</td>
                                    <td>{item.Model}</td>
                                    <td>{item.Serial}</td>
                                    <td>
                                        {editMode[item.id] ? (
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={item.Location}
                                                onChange={(e) => {
                                                    setAllItems(prevItems =>
                                                        prevItems.map(inv =>
                                                            inv.id === item.id
                                                                ? { ...inv, Location: e.target.value }
                                                                : inv
                                                        )
                                                    );
                                                }}
                                            />
                                        ) : (
                                            item.Location
                                        )}
                                    </td>
                                    <td>
                                        {editMode[item.id] ? (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => handleSaveClick(item)}
                                                className="me-2"
                                            >
                                                Save
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleEditClick(item.id)}
                                                    className="me-2"
                                                >
                                                    Edit
                                                </Button>
                                                {user && user.role === 'admin' && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteClick(item.id)}
                                                    >
                                                        <Trash className="me-1" />
                                                        Delete
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {renderPagination()}
            </Container>
        </div>
    );
};
/*
const AllList = () => {
    const [inventory, setInventory] = useState([]);
    const [editMode, setEditMode] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(apiUrl + '/inventory')
            .then(res => {
                setInventory(res.data);
                setLoading(false);
                console.log(res.data);
            })
            .catch(err => {
                console.log(err);
                setLoading(false);
            });
    }, []);

    const handleEditClick = (id) => {
        setEditMode(prevEditMode => ({
            ...prevEditMode,
            [id]: true
        }));
    };

    const handleSaveClick = (itemsedited) => {
        setEditMode(prevEditMode => ({
            ...prevEditMode,
            [itemsedited.id]: false
        }));
        axios.post(apiUrl + '/update-item', itemsedited)
            .then(res => {
                window.location.reload();
            })
            .catch(err => {
                console.log(err);
            });
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!Array.isArray(inventory)) {
        return <p>No data available</p>;
    }

    return (
        <div className="inventory-table">
            <table className="inventory-table">
                <thead>
                    <tr>
                        <th>Count</th>
                        <th>QR ID</th>
                        <th>Type</th>
                        <th>Make</th>
                        <th>Model</th>
                        <th>Location</th>
                        <th>Edit</th>
                    </tr>
                </thead>
                <tbody>
                    {inventory.map(inventory => (
                        <tr key={inventory.id}>
                          
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
*/
function Home() {
    return (
        <div>
            <Navigation />
            <AllList />
        </div>
    );
}

export default Home;
